import { FC } from 'react'
import { LuCalendarCheck, LuClock, LuDumbbell, LuFlame, LuGauge, LuRepeat, LuTimer, LuUsers } from 'react-icons/lu'
import { SectionHeading } from 'components/ui/Section'
import { formatFixed, formatInt, formatMinSec, ISummary, percentChange, safeDivide } from 'utils/stats'
import { RecordCardView } from './recordCardView'

interface IRangeSummary {
    summary: ISummary
    /** Previous equal-length period; undefined when it falls outside the 12-month data. */
    previous?: ISummary
    totalDays: number
    recordTotal?: number
    range: string
    loading: boolean
}

/** Section F: KPI tiles for the selected range. */
export const RangeSummary: FC<IRangeSummary> = ({ summary, previous, totalDays, recordTotal, range, loading }) => {
    const minutes = Math.round(summary.totalSeconds / 60)
    // KPIs carry a comparison when one is available (DESIGN.md principle 3)
    const delta = (pick: (s: ISummary) => number) => previous
        ? { showDelta: true, percentage: percentChange(pick(summary), pick(previous)), deltaLabel: '較前一期間', deltaEmptyLabel: '前一期間無資料' }
        : {}
    return <div className='space-y-4 lg:space-y-6'>
        <SectionHeading title='期間摘要' description='所選期間的整體運動數據' range={range} />
        <div className='grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6'>
            <RecordCardView
                title='期間運動次數'
                icon={<LuDumbbell />}
                value={formatInt(summary.sessions)}
                unit='次'
                {...delta((s) => s.sessions)}
                description={recordTotal != null ? `紀錄表總數 ${formatInt(recordTotal)} 筆` : undefined}
                loading={loading} />
            <RecordCardView
                title='期間總時數'
                icon={<LuClock />}
                value={formatInt(minutes)}
                unit='分鐘'
                {...delta((s) => s.totalSeconds)}
                description={minutes >= 60 ? `約 ${formatFixed(minutes / 60, 1)} 小時` : undefined}
                loading={loading} />
            <RecordCardView
                title='總完成次數'
                icon={<LuRepeat />}
                value={formatInt(summary.totalReps)}
                unit='下'
                {...delta((s) => s.totalReps)}
                loading={loading} />
            <RecordCardView
                title='平均每次運動時間'
                icon={<LuTimer />}
                value={formatMinSec(safeDivide(summary.totalSeconds, summary.sessions))}
                loading={loading} />
            <RecordCardView
                title='平均每次完成次數'
                icon={<LuGauge />}
                value={formatFixed(safeDivide(summary.totalReps, summary.sessions), 1)}
                unit={summary.sessions ? '下' : undefined}
                loading={loading} />
            <RecordCardView
                title='參與學生人數'
                icon={<LuUsers />}
                value={formatInt(summary.students)}
                unit='人'
                {...delta((s) => s.students)}
                description={`另有 ${formatInt(summary.unregisteredSessions)} 次未登記學生`}
                loading={loading} />
            <RecordCardView
                title='活躍日數'
                icon={<LuCalendarCheck />}
                value={formatInt(summary.activeDays)}
                unit={`/ ${formatInt(totalDays)} 日`}
                {...delta((s) => s.activeDays)}
                loading={loading} />
            <RecordCardView
                title='每日平均次數'
                icon={<LuFlame />}
                value={formatFixed(safeDivide(summary.sessions, summary.activeDays), 1)}
                unit={summary.activeDays ? '次 / 活躍日' : undefined}
                loading={loading} />
        </div>
    </div>
}
